import React, { forwardRef, useEffect, useState } from 'react';
import { LoadDocumentType } from '../../../redux/api/load/load.model';
import { SxProps, Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import useAuth from '../../../providers/Auth/Auth.provider';
import { TwDocumentType } from '../../../components/TwDocumentViewer/TwDocumentViewer';
import TwFile from '../../../components/TwFile/TwFile';
import {
  setExistingDocuments,
  updateAttachments,
  updateDocuments,
  updateExistingDocument
} from '../../../redux/features/orders/ordersSlice';
import DocumentUtils from '../../../utils/documentUtils';
import './Attachments.scss';
import { OrderAttachment, OrdersTripsDocumentParams } from '../../../redux/api/orders/orders.model';
import TwUploadOrderTripFiles from '../../../components/TwUploadOrderTripFiles/TwUploadOrderTripFiles';
import Stack from '@mui/material/Stack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TwOrderTripsDocViewer from '../../../components/TwOrderTripsDocViewer/TwOrderTripsDocViewer';
import { EntityType } from '../../../redux/Global.model';
import {
  setExistingTripDocuments,
  updateExistingTripDocument,
  updateTripAttachments,
  updateTripDocuments
} from '../../../redux/features/trip/tripSlice';

interface AttachmentsInfoProps {
  importAttachments?: OrderAttachment[];
  additionalContainerSx?: SxProps;
  onSubmit?: () => void;
  isImport?: boolean;
  fullScreenImport?: boolean;
  isCreateOrder?: boolean;
  entity: EntityType;
}
const fileOptions = [
  { label: 'Select type', value: LoadDocumentType.empty },
  ...DocumentUtils.getDocTypeOptions()
];

const AttachmentsInfo = forwardRef<HTMLDivElement, AttachmentsInfoProps>(
  (
    {
      importAttachments,
      additionalContainerSx,
      onSubmit,
      isCreateOrder,
      isImport,
      fullScreenImport,
      entity
    },
    ref
  ) => {
    const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const dispatch = useAppDispatch();
    const { getTenantSetting } = useAuth();
    const isEnabledDriversSeeDocs = getTenantSetting('allow_drivers_to_view_documents');

    // Orders selectors
    const {
      duplicate_load,
      existing_documents: existingDocsOrder,
      attachments: attachmentsOrder
    } = useAppSelector((state) => state.orders);

    // Trips selectors
    const { existing_documents: existingDocsTrip } = useAppSelector((state) => state.trip);
    const [existingDocumentsLocal, setExistingDocumentsLocal] = useState<
      OrderAttachment[] | undefined
    >([]);
    const [selectedAttachments, setSelectedAttachments] = useState<OrderAttachment[]>([]);
    const [uploadOpen, setUploadOpen] = useState<boolean>(false);
    const [selectedDocuments, setSelectedDocuments] = useState<OrdersTripsDocumentParams[] | never>(
      []
    );
    const [filePreviewOpen, setFilePreviewOpen] = useState<boolean>(false);
    const [selectedDocumentId, setSelectedDocumentId] = useState<string | undefined>(undefined);
    const [selectedFileToPreview, setSelectedFileToPreview] = useState<Blob | undefined>(undefined);
    const [selectedFileToPreviewTitle, setSelectedFileToPreviewTitle] = useState<
      string | undefined
    >(undefined);
    const [emailId, setEmailId] = useState<string | undefined>(undefined);

    // #region Functions
    const handleFilesToUpload = (files: OrdersTripsDocumentParams[]): void => {
      files?.map((file) => {
        setSelectedDocuments((prev) => [...prev, file]);
      });

      setUploadOpen(false);
    };

    /**
     * Opens document viewer for an attachment or file
     * @param document
     */
    const openPreview = async (document: OrdersTripsDocumentParams): Promise<void> => {
      setFilePreviewOpen(true);
      /* if ('emailId' in document && 'id' in document) {
        try {
          const file = await LoadsService.getEmailFile(document.emailId, document.id);
          document.name && setSelectedFileToPreviewTitle(document.name);
          file && setSelectedFileToPreview(file);
        } catch (err) {
          console.error(err);
        }
      } else { */
      setSelectedFileToPreview(document.document);
      setSelectedFileToPreviewTitle(document.name);
      // }
    };

    const closeFilePreview = (): void => {
      setSelectedFileToPreview(undefined);
      setFilePreviewOpen(false);
      setSelectedDocumentId(undefined);
    };

    // #endregion

    // #region Effects
    useEffect(() => {
      const attachments: OrderAttachment[] = [];

      if (attachmentsOrder && entity === EntityType.ORDER) {
        importAttachments?.map((att) => {
          attachments.push(att);
        });
        // TODO: Review this if email import is required again
        // attachments[0] && setEmailId(attachments[0].emailId);
        setSelectedAttachments(attachments);
      }
    }, [attachmentsOrder]);

    useEffect(() => {
      if (entity === EntityType.ORDER) {
        dispatch(updateDocuments(selectedDocuments));
      }
      if (entity === EntityType.TRIP) {
        dispatch(updateTripDocuments(selectedDocuments));
      }
    }, [selectedDocuments]);

    useEffect(() => {
      if (entity === EntityType.TRIP) {
        setExistingDocumentsLocal(existingDocsTrip);
      }

      if (entity === EntityType.ORDER) {
        setExistingDocumentsLocal(duplicate_load ? [] : existingDocsOrder);
      }
    }, [duplicate_load, entity, existingDocsOrder, existingDocsTrip]);

    // #endregion

    return (
      <>
        <Box
          ref={ref}
          component="div"
          className={`${
            (isImport && !fullScreenImport) || matchesMobileOrTablet
              ? 'route-info__main-import'
              : 'route-info__main'
          } `}
        >
          <span
            className={
              (isImport && !fullScreenImport) || matchesMobileOrTablet
                ? 'route-info__title-left'
                : 'route-info__title'
            }
          >
            Attachments
          </span>
          <span
            className={
              isImport || matchesMobileOrTablet
                ? 'route-info__sub-title-left'
                : 'route-info__sub-title'
            }
          >
            Upload rate confirmation or other related documents
          </span>

          <Box sx={additionalContainerSx}>
            <Box component="div" className="load-detail__data-container__data__attachments">
              <Stack
                className="load-detail__data-container__data__attachments--container-2"
                onClick={(): void => {
                  setUploadOpen(true);
                }}
              >
                <p className="load-detail__data-container__data__attachments--text">
                  <CloudUploadIcon
                    className="load-detail__data-container__data__attachments--icon"
                    sx={{ marginRight: '10px' }}
                  />
                  Click here to{' '}
                  <span className="load-detail__data-container__data__attachments--highlited-text">
                    upload a file
                  </span>
                </p>
                <p className="load-detail__data-container__data__attachments--secondaryText">
                  Files Supported: PDF, XSLS, JPG, PNG, maximum size 200MB{' '}
                </p>
              </Stack>
            </Box>

            {selectedDocuments &&
              selectedDocuments.length > 0 &&
              selectedDocuments.map((file, i) => {
                return (
                  <div key={file.name} className="additional-info__document">
                    <div className="additional-info__document-info">
                      <TwFile
                        id={`${file.name}-${i}`}
                        name={file.name ? file.name : 'Attachment'}
                        file={file.document}
                        clearInputAction={(): void => {
                          const newList = selectedDocuments.filter(
                            (item) => item.document.name !== file.document.name
                          );
                          setSelectedDocuments(newList);
                        }}
                        selectValue={file.type}
                        optionsSelectList={fileOptions}
                        handleSelectChange={(e): void => {
                          const indexToUpdate = selectedDocuments?.findIndex((item) => {
                            return item.name === file.name;
                          });

                          if (indexToUpdate >= 0) {
                            const docs = [...selectedDocuments];
                            const value = docs.find((doc) => {
                              return doc.name === file.name && doc.type === file.type;
                            });

                            if (value) {
                              docs[indexToUpdate] = {
                                ...value,
                                type: e.target.value as LoadDocumentType
                              };

                              setSelectedDocuments(docs);
                            }
                          }
                        }}
                        showTypeSelect
                        showVisibilitySelect={isCreateOrder ? false : true}
                        visibilityValue={file.visibility || false}
                        handleVisibilityChange={(e): void => {
                          const indexToUpdate = selectedDocuments?.findIndex((item) => {
                            return item.name === file.name;
                          });

                          if (indexToUpdate >= 0) {
                            const docs = [...selectedDocuments];
                            const value = docs.find((doc) => {
                              return doc.name === file.name && doc.type === file.type;
                            });

                            if (value) {
                              docs[indexToUpdate] = {
                                ...value,
                                visibility: e.target.value === 'visible' ? true : false
                              };

                              setSelectedDocuments(docs);
                            }
                          }
                        }}
                        onClick={(): Promise<void> => openPreview(file)}
                      />
                    </div>
                  </div>
                );
              })}

            {existingDocumentsLocal &&
              existingDocumentsLocal.length > 0 &&
              existingDocumentsLocal.map((document) => (
                <div key={document.id} className="additional-info__document">
                  <div className="additional-info__document-info">
                    <TwFile
                      id={document.id}
                      name={document.attachment.name ? document.attachment.name : 'Attachment'}
                      file={document.attachment}
                      clearInputAction={(): void => {
                        const newExistingDocuments = existingDocumentsLocal.filter(
                          (doc) => doc.id !== document.id
                        );
                        entity === EntityType.ORDER
                          ? dispatch(setExistingDocuments(newExistingDocuments))
                          : dispatch(setExistingTripDocuments(newExistingDocuments));
                      }}
                      optionsSelectList={fileOptions}
                      handleSelectChange={(e): void => {
                        entity === EntityType.ORDER
                          ? dispatch(
                              updateExistingDocument({
                                ...document,
                                type: e.target.value as LoadDocumentType,
                                updated_at: Date.now().toString() // use this here for recognize the existingDocument updated
                              })
                            )
                          : dispatch(
                              updateExistingTripDocument({
                                ...document,
                                type: e.target.value as LoadDocumentType,
                                updated_at: Date.now().toString() // use this here for recognize the existingDocument updated
                              })
                            );
                      }}
                      selectValue={document.type}
                      showTypeSelect
                      showVisibilitySelect={isCreateOrder ? false : true}
                      visibilityValue={document.visibility || false}
                      handleVisibilityChange={(e): void => {
                        entity === EntityType.ORDER
                          ? dispatch(
                              updateExistingDocument({
                                ...document,
                                visibility: e.target.value === 'visible' ? true : false,
                                updated_at: Date.now().toString()
                              })
                            )
                          : dispatch(
                              updateExistingTripDocument({
                                ...document,
                                visibility: e.target.value === 'visible' ? true : false,
                                updated_at: Date.now().toString()
                              })
                            );
                      }}
                      onClick={(): void => {
                        setFilePreviewOpen(true);
                        setSelectedDocumentId(document.id);
                      }}
                    />
                  </div>
                </div>
              ))}

            {selectedAttachments &&
              selectedAttachments.length > 0 &&
              selectedAttachments?.map((document) => {
                return (
                  <div key={document.attachment.name} className="additional-info__document">
                    <div className="additional-info__document-info">
                      <TwFile
                        id={document.attachment.key}
                        name={document.attachment.name ? document.attachment.name : 'Attachment'}
                        file={document.attachment}
                        clearInputAction={(): void => {
                          const newAttachmentList = selectedAttachments.filter(
                            (item) => item.id !== document.attachment.id
                          );
                          setSelectedAttachments(newAttachmentList);
                          entity === EntityType.ORDER
                            ? dispatch(updateAttachments(newAttachmentList))
                            : dispatch(updateTripAttachments(newAttachmentList));
                        }}
                        selectValue={document.attachment.file_type}
                        optionsSelectList={fileOptions}
                        handleSelectChange={(e): void => {
                          const valueSelected = selectedAttachments?.find((item) => {
                            return item.id === document.attachment.id;
                          });

                          if (
                            valueSelected?.attachment.name === document.attachment.name &&
                            valueSelected?.attachment.size === document.attachment.size
                          ) {
                            const newAttachmentList = selectedAttachments.map((item) => {
                              if (valueSelected && item.id === valueSelected.id) {
                                item.attachment.file_type = e.target.value as LoadDocumentType;
                                return item;
                              }
                              return item;
                            });
                            setSelectedAttachments(newAttachmentList);
                            entity === EntityType.ORDER
                              ? dispatch(updateAttachments(newAttachmentList))
                              : dispatch(updateTripAttachments(newAttachmentList));
                          }
                        }}
                        showTypeSelect
                        showVisibilitySelect={isCreateOrder ? false : true}
                        visibilityValue={document.visibility || false}
                        handleVisibilityChange={(e): void => {
                          const valueSelected = selectedAttachments?.find((item) => {
                            return item.id === document.attachment.id;
                          });

                          if (
                            valueSelected?.attachment.name === document.attachment.name &&
                            valueSelected?.attachment.size === document.attachment.size
                          ) {
                            const newAttachmentList = selectedAttachments.map((item) => {
                              if (valueSelected && item.id === valueSelected.id) {
                                item.visibility = e.target.value === 'visible' ? true : false;
                                return item;
                              }
                              return item;
                            });
                            setSelectedAttachments(newAttachmentList);

                            entity === EntityType.ORDER
                              ? dispatch(updateAttachments(newAttachmentList))
                              : dispatch(updateTripAttachments(newAttachmentList));
                          }
                        }}
                        onClick={(): void => {
                          setFilePreviewOpen(true);
                          setSelectedDocumentId(document.id);
                        }}
                      />
                    </div>
                  </div>
                );
              })}
          </Box>
        </Box>

        <TwUploadOrderTripFiles
          open={uploadOpen}
          entity={entity === EntityType.ORDER ? 'order' : 'trip'}
          defaultVisibility={isEnabledDriversSeeDocs}
          handleClose={(): void => {
            setUploadOpen(false);
          }}
          entityList={[]}
          orderId=""
          disableInternalUpload={true}
          handleFilesToUpload={handleFilesToUpload}
          hideEntitySelector={true}
        />

        <TwOrderTripsDocViewer
          readableId={0}
          selectedFileId={selectedDocumentId}
          emailId={emailId}
          files={[...(existingDocumentsLocal ?? [])]}
          blob={selectedFileToPreview}
          blobTitle={selectedFileToPreviewTitle}
          blobType={
            selectedFileToPreview && selectedFileToPreview.type.includes('pdf')
              ? TwDocumentType.pdf
              : TwDocumentType.image
          }
          open={filePreviewOpen}
          onClickClose={closeFilePreview}
        />
      </>
    );
  }
);
AttachmentsInfo.displayName = 'AttachmentsInfo';
export default AttachmentsInfo;
